import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useServices } from '@/context/ServiceContext'
import { useAuth } from '@/context/AuthContext'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { domainEvents } from '@/services/events/domainEvents'
import type { EscapeRoom, EscapeRoomAttempt } from '@/domain/escapeRoom'

function formatClock(totalSeconds: number): string {
  const clamped = Math.max(0, totalSeconds)
  const m = Math.floor(clamped / 60)
  const s = clamped % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

// P4 Escape Room player (Document 11 §P4). All content — briefing,
// challenges, evidence, answers, hints — comes from `room.challenges`; this
// component has no idea what "phishing" or "SIM-swap" mean, it just renders
// whichever challenge is next and checks the answer against the data. Every
// piece of "evidence" rendered here is simulated text (Escape Room Safety
// requirement) — nothing here ever touches a real system.
export function EscapeRoomPage() {
  const { id } = useParams<{ id: string }>()
  const { escapeRooms, escapeRoomAttempts, xp, badges } = useServices()
  const { currentUser } = useAuth()
  const navigate = useNavigate()

  const [room, setRoom] = useState<EscapeRoom | null>(null)
  const [attempt, setAttempt] = useState<EscapeRoomAttempt | null>(null)
  const [loading, setLoading] = useState(true)
  const [now, setNow] = useState(Date.now())
  const [answerInput, setAnswerInput] = useState('')
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null)
  const [revealedHintForChallengeId, setRevealedHintForChallengeId] = useState<string | null>(null)
  const [xpJustEarned, setXpJustEarned] = useState(0)
  const [newBadgeKeys, setNewBadgeKeys] = useState<string[]>([])

  useEffect(() => {
    if (!id || !currentUser) return
    let cancelled = false
    Promise.all([escapeRooms.getById(id), escapeRoomAttempts.getActiveAttempt(currentUser.id, id)]).then(
      ([r, a]) => {
        if (cancelled) return
        setRoom(r)
        setAttempt(a)
        setLoading(false)
      }
    )
    return () => {
      cancelled = true
    }
  }, [id, currentUser, escapeRooms, escapeRoomAttempts])

  useEffect(() => {
    if (!attempt || attempt.status !== 'in_progress') return
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [attempt])

  const remainingSeconds = useMemo(() => {
    if (!attempt) return 0
    return Math.floor((new Date(attempt.expiresAt).getTime() - now) / 1000)
  }, [attempt, now])

  useEffect(() => {
    if (!attempt || attempt.status !== 'in_progress' || remainingSeconds > 0) return
    const failed: EscapeRoomAttempt = { ...attempt, status: 'failed' }
    setAttempt(failed)
    escapeRoomAttempts.saveAttempt(failed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingSeconds])

  if (loading) return <LoadingSpinner label="Loading mission…" />
  if (!room || !currentUser) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-white">Escape room not found</h1>
        <Link to="/missions" className="cyber-btn-secondary mt-6 inline-flex">
          ← Back to Missions
        </Link>
      </div>
    )
  }

  const orderedChallenges = [...room.challenges].sort((a, b) => a.order - b.order)

  async function handleStart() {
    if (!currentUser || !room) return
    const started = Date.now()
    const newAttempt: EscapeRoomAttempt = {
      id: crypto.randomUUID(),
      userId: currentUser.id,
      orgId: currentUser.orgId,
      roomId: room.id,
      startedAt: new Date(started).toISOString(),
      expiresAt: new Date(started + room.timeLimitSeconds * 1000).toISOString(),
      solvedChallengeIds: [],
      hintsUsed: 0,
      status: 'in_progress',
      completedAt: null,
    }
    await escapeRoomAttempts.startAttempt(newAttempt)
    setAttempt(newAttempt)
    setNow(started)
    setAnswerInput('')
    setFeedback(null)
    setXpJustEarned(0)
    setNewBadgeKeys([])
  }

  async function handleSubmitAnswer(selectedOptionId?: string) {
    if (!attempt || attempt.status !== 'in_progress' || !currentUser || !room) return
    const currentChallenge = orderedChallenges[attempt.solvedChallengeIds.length]
    if (!currentChallenge) return

    const isCorrect =
      currentChallenge.type === 'multiple_choice'
        ? selectedOptionId === currentChallenge.correctOptionId
        : (currentChallenge.acceptedAnswers ?? []).includes(answerInput.trim().toLowerCase())

    if (!isCorrect) {
      setFeedback('incorrect')
      return
    }
    setFeedback('correct')

    const solvedChallengeIds = [...attempt.solvedChallengeIds, currentChallenge.id]
    const isRoomComplete = solvedChallengeIds.length === orderedChallenges.length
    const updated: EscapeRoomAttempt = {
      ...attempt,
      solvedChallengeIds,
      status: isRoomComplete ? 'completed' : 'in_progress',
      completedAt: isRoomComplete ? new Date().toISOString() : null,
    }
    await escapeRoomAttempts.saveAttempt(updated)
    setAttempt(updated)
    setAnswerInput('')

    if (isRoomComplete) {
      const timeTakenSeconds = Math.round((new Date(updated.completedAt!).getTime() - new Date(updated.startedAt).getTime()) / 1000)
      const [xpBefore, badgesBefore] = await Promise.all([xp.getTotalForUser(currentUser.id), badges.listAwardsForUser(currentUser.id)])
      await domainEvents.emit('escaperoom.completed', {
        userId: currentUser.id,
        orgId: currentUser.orgId,
        roomId: room.id,
        timeTakenSeconds,
        hintsUsed: updated.hintsUsed,
      })
      const [xpAfter, badgesAfter] = await Promise.all([xp.getTotalForUser(currentUser.id), badges.listAwardsForUser(currentUser.id)])
      setXpJustEarned(xpAfter - xpBefore)
      const beforeKeys = new Set(badgesBefore.map((b) => b.badgeKey))
      setNewBadgeKeys(badgesAfter.filter((b) => !beforeKeys.has(b.badgeKey)).map((b) => b.badgeKey))
    } else {
      setTimeout(() => setFeedback(null), 400)
    }
  }

  async function handleRevealHint(challengeId: string) {
    if (!attempt) return
    setRevealedHintForChallengeId(challengeId)
    const updated: EscapeRoomAttempt = { ...attempt, hintsUsed: attempt.hintsUsed + 1 }
    await escapeRoomAttempts.saveAttempt(updated)
    setAttempt(updated)
  }

  // ---- Briefing / not-started state ----
  if (!attempt) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8">
        <Link to="/missions" className="text-sm text-white/50 hover:text-white">
          ← Missions
        </Link>
        <div className="mt-4 cyber-panel p-8 text-center">
          <span className="cyber-pill bg-coral-500/15 text-coral-500 ring-1 ring-coral-500/30">Escape Room</span>
          <h1 className="mt-4 font-display text-3xl font-bold text-white">{room.title}</h1>
          <p className="mt-4 text-white/70">{room.briefing}</p>
          <p className="mt-4 text-sm text-white/50">
            Time limit: {formatClock(room.timeLimitSeconds)} · {orderedChallenges.length} challenges, solved in order
          </p>
          <button onClick={handleStart} className="cyber-btn-primary mt-6">
            Start Mission
          </button>
        </div>
      </div>
    )
  }

  // ---- Failed state ----
  if (attempt.status === 'failed') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8 text-center">
        <div className="cyber-panel p-8">
          <span className="text-4xl">⏱️</span>
          <h1 className="mt-4 font-display text-2xl font-bold text-white">Time's up</h1>
          <p className="mt-3 text-white/60">
            You solved {attempt.solvedChallengeIds.length} of {orderedChallenges.length} challenges before the clock ran
            out. Real incident response is exactly this — fast, methodical, under pressure. Take another run at it.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button onClick={handleStart} className="cyber-btn-primary">
              Try Again
            </button>
            <button onClick={() => navigate('/missions')} className="cyber-btn-secondary">
              Back to Missions
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ---- Completed state ----
  if (attempt.status === 'completed') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8 text-center">
        <div className="cyber-panel p-8">
          <span className="text-4xl">🗝️</span>
          <h1 className="mt-4 font-display text-2xl font-bold text-white">Breach Contained</h1>
          <p className="mt-3 text-white/60">
            All {orderedChallenges.length} challenges solved
            {attempt.hintsUsed > 0 && ` (${attempt.hintsUsed} hint${attempt.hintsUsed === 1 ? '' : 's'} used)`}.
          </p>
          {xpJustEarned > 0 && <p className="cyber-pill mx-auto mt-4 w-fit text-amber-300">+{xpJustEarned} Cyber Points earned</p>}
          {newBadgeKeys.length > 0 && <p className="mt-3 text-sm text-white/70">New achievement unlocked: {newBadgeKeys.join(', ')}</p>}
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button onClick={handleStart} className="cyber-btn-secondary">
              Play Again
            </button>
            <button onClick={() => navigate('/missions')} className="cyber-btn-primary">
              Back to Missions
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ---- In-progress state ----
  const currentChallenge = orderedChallenges[attempt.solvedChallengeIds.length]
  const progressPercent = Math.round((attempt.solvedChallengeIds.length / orderedChallenges.length) * 100)
  const timeCritical = remainingSeconds <= 60

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between">
        <Link to="/missions" className="text-sm text-white/50 hover:text-white">
          ← Missions
        </Link>
        <span className={`font-display text-lg font-bold ${timeCritical ? 'text-coral-500' : 'text-white'}`}>
          {formatClock(remainingSeconds)}
        </span>
      </div>

      <h1 className="mt-3 font-display text-2xl font-bold text-white">{room.title}</h1>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <div className="h-full rounded-full bg-gradient-to-r from-teal-400 to-coral-500 transition-all" style={{ width: `${progressPercent}%` }} />
      </div>
      <p className="mt-1 text-xs text-white/40">
        Challenge {attempt.solvedChallengeIds.length + 1} of {orderedChallenges.length}
      </p>

      <div className="mt-6 cyber-panel p-6">
        <h2 className="font-display font-semibold text-white">{currentChallenge.title}</h2>
        <p className="mt-2 text-sm text-white/70">{currentChallenge.prompt}</p>

        <div className="mt-4 space-y-3">
          {currentChallenge.evidence.map((ev) => (
            <div key={ev.id} className="rounded-xl bg-black/30 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-teal-300">{ev.label}</p>
              <pre className="mt-1 whitespace-pre-wrap font-mono text-xs text-white/80">{ev.content}</pre>
            </div>
          ))}
        </div>

        {currentChallenge.type === 'multiple_choice' ? (
          <div className="mt-5 space-y-2">
            {currentChallenge.options?.map((opt) => (
              <button
                key={opt.id}
                onClick={() => handleSubmitAnswer(opt.id)}
                className="block w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-left text-sm text-white/85 transition hover:border-teal-400/40"
              >
                {opt.text}
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-5 flex gap-2">
            <input
              value={answerInput}
              onChange={(e) => setAnswerInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmitAnswer()}
              placeholder="Type your answer…"
              className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-teal-400/50 focus:outline-none"
            />
            <button onClick={() => handleSubmitAnswer()} className="cyber-btn-primary !px-4 !py-2 text-sm">
              Submit
            </button>
          </div>
        )}

        {feedback === 'incorrect' && (
          <p className="mt-3 text-sm text-coral-500">Not quite — check the evidence again, or use a hint.</p>
        )}
        {feedback === 'correct' && <p className="mt-3 text-sm text-emerald-300">✓ {currentChallenge.explanation}</p>}

        <div className="mt-4">
          {revealedHintForChallengeId === currentChallenge.id ? (
            <p className="text-xs text-amber-300">💡 {currentChallenge.hint}</p>
          ) : (
            <button onClick={() => handleRevealHint(currentChallenge.id)} className="text-xs text-white/40 hover:text-white/70">
              Show a hint
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
